<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                :pages="[
                    {
                        name: $tc('messages.fouls', 2),
                        href: meta.links.index,
                    },
                    {
                        name: group.name,
                        href: null,
                    },
                    {
                        name: $t('messages.create'),
                        href: null,
                    },
                ]"
            />
        </template>

        <AppWell>
            <StudentsProfileStackFiltered
                :students="group.students"
                :selected="form.students"
                class="inline-block h-8"
            />
        </AppWell>

        <UnsavedChangesFormWarning :is-dirty="form.isDirty" v-if="form.isDirty" />
        <form :action="meta.links.update" method="post" @submit.prevent="submit">
            <div class="md:grid md:grid-cols-3 md:gap-6">
                <div class="mt-5 md:mt-0 md:col-span-2">
                    <div class="md:grid md:grid-cols-6 md:gap-6">
                        <div class="sm:col-span-2">
                            <div class="mt-1">
                                <FormLabel :value="$tc('messages.subject', 1)" for="subject_id" />
                                <FormSelect
                                    id="subject_id"
                                    v-model="form.subject_id"
                                    :options="options.subjects"
                                    class="w-full mt-1"
                                />
                                <FormError :value="form.errors.subject_id" class="mt-2" />
                            </div>
                        </div>

                        <div class="sm:col-span-2">
                            <div class="mt-1">
                                <FormLabel :value="$tc('messages.places', 1)" for="place" />
                                <FormInput
                                    id="place"
                                    v-model="form.place"
                                    name="place"
                                    type="text"
                                    list="places_suggestions"
                                    class="w-full mt-1"
                                />
                                <FormError :value="form.errors.place" class="mt-2" />
                                <datalist id="places_suggestions">
                                    <option v-for="(place, index) in options.places" :value="place" :key="index" />
                                </datalist>
                            </div>
                        </div>

                        <div class="sm:col-span-2">
                            <div class="mt-1">
                                <FormLabel :value="$t('messages.date')" for="date" />
                                <FormInput
                                    required
                                    id="date"
                                    v-model="form.date"
                                    name="date"
                                    type="date"
                                    class="w-full mt-1"
                                />
                                <FormError :value="form.errors.date" class="mt-2" />
                            </div>
                        </div>

                        <div class="sm:col-span-2">
                            <div class="mt-1">
                                <FormLabel :value="$t('messages.time')" for="time" />
                                <FormInput
                                    required
                                    id="time"
                                    v-model="form.time"
                                    name="time"
                                    type="time"
                                    class="w-full mt-1"
                                />
                                <FormError :value="form.errors.time" class="mt-2" />
                            </div>
                        </div>

                        <div class="sm:col-span-2">
                            <div class="mt-1">
                                <FormLabel :value="$t('messages.turn')" for="turn" />
                                <div class="mt-1">
                                    <label>
                                        <input
                                            type="radio"
                                            id="turn_morning"
                                            value="0"
                                            name="turn"
                                            v-model="form.turn"
                                            class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                                        />
                                        {{ $t('messages.morning') }}
                                    </label>
                                    <label>
                                        <input
                                            type="radio"
                                            id="turn_afternoon"
                                            value="1"
                                            name="turn"
                                            v-model="form.turn"
                                            class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                                        />
                                        {{ $t('messages.afternoon') }}
                                    </label>
                                </div>
                                <FormError :value="form.errors.turn" class="mt-2" />
                            </div>
                        </div>

                        <div class="sm:col-span-2">
                            <div class="mt-1">
                                <FormLabel :value="$t('messages.type')" for="type" />
                                <FormSelect
                                    id="type"
                                    required
                                    v-model="form.type"
                                    :options="options.types"
                                    class="w-full mt-1"
                                />
                                <FormError :value="form.errors.type" class="mt-2" />
                            </div>
                        </div>

                        <div class="sm:col-span-2">
                            <div class="mt-1">
                                <FormLabel :value="$t('messages.severity')" for="severity" />
                                <FormSelect
                                    id="severity"
                                    required
                                    v-model="form.severity"
                                    :options="options.severities"
                                    class="w-full mt-1"
                                />
                                <FormError :value="form.errors.severity" class="mt-2" />
                            </div>
                        </div>

                        <div class="sm:col-span-6">
                            <div class="mt-1">
                                <FormLabel :value="$t('messages.internal_teacher_note')" for="internal_teacher_note">
                                    <SuggestionsPanel
                                        :suggestions="suggestions"
                                        @select-suggestion="(x) => (form.internal_teacher_note += x)"
                                    />
                                </FormLabel>
                                <FormTextarea
                                    rows="3"
                                    id="internal_teacher_note"
                                    v-model="form.internal_teacher_note"
                                    name="internal_teacher_note"
                                    type="text"
                                    class="mt-1 w-full"
                                />
                                <FormError :value="form.errors.internal_teacher_note" class="mt-2" />
                            </div>
                        </div>

                        <div class="sm:col-span-6">
                            <div class="mt-1">
                                <FormLabel :value="$t('messages.public_description')" for="public_description">
                                    <SuggestionsPanel
                                        :suggestions="suggestions"
                                        @select-suggestion="(x) => (form.public_description += x)"
                                    />
                                </FormLabel>
                                <FormTextarea
                                    rows="3"
                                    id="public_description"
                                    v-model="form.public_description"
                                    name="public_description"
                                    type="text"
                                    class="mt-1 alert-background-icon w-full"
                                />
                                <FormError :value="form.errors.public_description" class="mt-2" />
                            </div>
                        </div>

                        <div class="sm:col-span-6">
                            <div class="mt-1">
                                <FormLabel :value="$t('messages.punishment')" for="punishment" />
                                <FormTextarea
                                    rows="3"
                                    id="punishment"
                                    v-model="form.punishment"
                                    name="punishment"
                                    type="text"
                                    class="mt-1 w-full"
                                />
                                <FormError :value="form.errors.punishment" class="mt-2" />
                            </div>
                        </div>

                        <div class="sm:col-span-6 mt-3">
                            <label class="block text-sm font-medium leading-5 text-gray-700">
                                {{ $tc('messages.links', 2) }}
                            </label>
                            <div class="mt-1">
                                <LinkBatchCreate v-model="form.links" :links-input="[]" />
                            </div>
                        </div>
                    </div>
                </div>

                <div class="mt-5 md:mt-0 bg-gray-50">
                    <GroupStudentsSelector :students="group.students" v-model="form.students" />
                </div>
            </div>

            <input type="hidden" name="group_id" v-model="form.group_id" />

            <div class="flex justify-end mt-5 space-x-2">
                <AppButton
                    element="button"
                    type="submit"
                    @click="another = false"
                    :disabled="form.processing"
                    desired-action
                >
                    {{ $tc('messages.issue_fouls', form.students.length) }}
                    <span v-if="form.processing" class="ml-1 btn-spinner"></span>
                </AppButton>

                <AppButton
                    element="button"
                    type="submit"
                    @click="another = true"
                    :disabled="form.processing"
                    desired-action
                >
                    {{ $t('messages.issue_foul_and_another') }}
                    <span v-if="form.processing" class="ml-1 btn-spinner"></span>
                </AppButton>
            </div>
        </form>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import FormTextarea from '@/components/Forms/FormTextarea.vue';
import GroupStudentsSelector from '@/components/GroupStudentsSelector.vue';
import LinkBatchCreate from '@/components/pageSpecific/LinkBatchCreate.vue';
import SuggestionsPanel from '@/components/SuggestionsPanel.vue';
import AppButton from '@/components/AppButton.vue';
import UnsavedChangesFormWarning from '@/components/Forms/UnsavedChangesFormWarning.vue';
import StudentsProfileStackFiltered from '@/components/StudentsProfileStackFiltered.vue';
import AppWell from '@/Shared/AppWell.vue';

export default {
    components: {
        AppWell,
        StudentsProfileStackFiltered,
        UnsavedChangesFormWarning,
        PanelPage,
        FormInput,
        FormLabel,
        FormError,
        FormSelect,
        FormTextarea,
        GroupStudentsSelector,
        LinkBatchCreate,
        SuggestionsPanel,
        AppButton,
    },
    layout: Layout,
    name: 'Create',
    props: ['meta', 'options', 'group', 'defaults', 'suggestions'],
    data() {
        return {
            form: this.$inertia.form({
                type: '',
                severity: '',
                date: this.defaults.date,
                time: this.defaults.time,
                turn: this.defaults.turn,
                internal_teacher_note: '',
                public_description: '',
                punishment: '',
                place: this.defaults.place,
                subject_id: '',
                group_id: this.group.id,
                students: this.meta.defaults.student_ids.map((x) => +x),
                links: [],
            }),
            another: false,
        };
    },
    methods: {
        submit() {
            const url = this.another ? this.meta.links.store_and_another : this.meta.links.store;
            this.form.post(url, {
                onSuccess: () => this.form.reset(),
            });
        },
    },
};
</script>
